/**
 * Layout *renderer + controller* — turns the pure layout model
 * (`layout.ts`) into real DOM, and owns the live `stage` state that drives
 * which layout is shown.
 *
 * Split rule (see the terminology note in `layout.ts`):
 *   - orientation "horizontal" → a horizontal divider → panes stacked
 *     top/bottom → `flex-direction: column`.
 *   - orientation "vertical"   → a vertical divider → panes side by side
 *     left/right → `flex-direction: row`.
 *
 * Resizing is done with lightweight pointer-drag handles inserted between
 * sibling regions; on narrow screens CSS collapses every split to a vertical
 * stack and hides the handles (see `style.css`). All drag logic is guarded so
 * it is inert under the no-DOM test harness — only the structure is asserted
 * there.
 */
import {
  collectPanes,
  layoutForStage,
  type LayoutNode,
  type Stage,
} from "./layout";

/** Public surface for driving the layout from the app shell (and tests). */
export interface LayoutController {
  /** The stage currently rendered. */
  readonly stage: Stage;
  /** Render (or re-render) the current stage's layout into `container`. */
  mount(container: HTMLElement): void;
  /** Switch stages and re-render. No-op (and no event) if already on `stage`. */
  setStage(stage: Stage): void;
  /** Subscribe to stage changes. Returns an unsubscribe fn. */
  onStageChange(listener: (stage: Stage) => void): () => void;
}

export interface LayoutControllerOptions {
  /** Initial stage (default 1). */
  stage?: Stage;
}

/* ------------------------------- rendering -------------------------------- */

/** Recursively render a layout node to an element tree, resizers and all. */
export function renderLayout(doc: Document, node: LayoutNode): HTMLElement {
  if (node.kind === "pane") {
    const pane = doc.createElement("section");
    pane.className = "pane";
    pane.dataset.paneId = node.id;
    pane.dataset.paneLabel = node.label;
    pane.setAttribute("aria-label", node.label);
    pane.style.flexGrow = String(node.weight);
    pane.style.flexBasis = "0";

    // A labelled placeholder header so #701/#702/#710 have an obvious slot.
    const header = doc.createElement("header");
    header.className = "pane__label";
    header.textContent = node.label;
    pane.append(header);

    const body = doc.createElement("div");
    body.className = "pane__body";
    body.dataset.slot = node.id; // downstream tasks mount real content here.
    pane.append(body);

    return pane;
  }

  const splitEl = doc.createElement("div");
  splitEl.className = "split";
  splitEl.dataset.orientation = node.orientation;
  splitEl.style.flexGrow = String(node.weight);
  splitEl.style.flexBasis = "0";

  node.children.forEach((child, i) => {
    if (i > 0) {
      // Insert a draggable resizer between each pair of siblings.
      const resizer = doc.createElement("div");
      resizer.className = "split__resizer";
      resizer.dataset.role = "resizer";
      resizer.setAttribute("role", "separator");
      resizer.setAttribute(
        "aria-orientation",
        node.orientation === "horizontal" ? "horizontal" : "vertical",
      );
      splitEl.append(resizer);
    }
    splitEl.append(renderLayout(doc, child));
  });

  // Wire drag behaviour after children exist (browser-only; inert in tests).
  attachResizers(splitEl, node.orientation);

  return splitEl;
}

/**
 * Attach pointer-drag resizing to every resizer in a split. Each resizer
 * redistributes flex-grow between its immediate previous/next element siblings.
 * Guarded so the no-DOM test harness (no pointer events, no `window`) is unaffected.
 */
function attachResizers(splitEl: HTMLElement, orientation: string): void {
  const win = (globalThis as { window?: Window }).window;
  if (!win) return; // node/test environment — nothing to drag.

  const resizers = Array.from(
    splitEl.querySelectorAll?.(":scope > .split__resizer") ?? [],
  ) as HTMLElement[];

  for (const resizer of resizers) {
    resizer.addEventListener("pointerdown", (event) => {
      const before = resizer.previousElementSibling as HTMLElement | null;
      const after = resizer.nextElementSibling as HTMLElement | null;
      if (!before || !after) return;

      const e = event as PointerEvent;
      e.preventDefault();
      const vertical = orientation === "vertical"; // side-by-side → drag on X

      const rect = splitEl.getBoundingClientRect();
      const total = vertical ? rect.width : rect.height;
      const startPos = vertical ? e.clientX : e.clientY;
      const startBefore = parseFloat(before.style.flexGrow || "1");
      const startAfter = parseFloat(after.style.flexGrow || "1");
      const sum = startBefore + startAfter;

      const onMove = (move: PointerEvent): void => {
        const delta = (vertical ? move.clientX : move.clientY) - startPos;
        if (total <= 0) return;
        const shift = (delta / total) * sum;
        const next = Math.min(Math.max(startBefore + shift, 0.1), sum - 0.1);
        before.style.flexGrow = String(next);
        after.style.flexGrow = String(sum - next);
      };
      const onUp = (): void => {
        win.removeEventListener("pointermove", onMove);
        win.removeEventListener("pointerup", onUp);
      };
      win.addEventListener("pointermove", onMove);
      win.addEventListener("pointerup", onUp);
    });
  }
}

/* ------------------------------- controller ------------------------------- */

/** Create a controller that renders stage layouts and manages stage state. */
export function createLayoutController(
  doc: Document,
  options: LayoutControllerOptions = {},
): LayoutController {
  let stage: Stage = options.stage ?? 1;
  let container: HTMLElement | null = null;
  const listeners = new Set<(stage: Stage) => void>();

  function render(): void {
    if (!container) return;
    const tree = renderLayout(doc, layoutForStage(stage));
    tree.dataset.layoutRoot = "true";
    tree.dataset.stage = String(stage);
    container.replaceChildren(tree);
  }

  return {
    get stage() {
      return stage;
    },
    mount(target: HTMLElement) {
      container = target;
      render();
    },
    setStage(next: Stage) {
      if (next === stage) return;
      stage = next;
      render();
      for (const listener of listeners) listener(stage);
    },
    onStageChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** Convenience: panes present at a stage (handy for downstream wiring/tests). */
export function panesForStage(stage: Stage): string[] {
  return collectPanes(layoutForStage(stage)).map((p) => p.id);
}
